import { Instagram, Youtube, Facebook, Mail } from "lucide-react";
import { FadeUp } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FlameInitial } from "../components/FlameInitial";
import { CornerWebs } from "../components/CornerWebs";
import { CastingJoinSection } from "../components/CastingJoinSection";
import { CONTACT_EMAIL } from "../lib/content";

const TikTokIcon = ({ size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const XIcon = ({ size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const CHANNELS = [
  { Icon: Youtube, label: "YouTube", handle: "@tattooedabroad", href: "https://www.youtube.com/@tattooedabroad" },
  { Icon: TikTokIcon, label: "TikTok", handle: "@tattooedabroad", href: "https://www.tiktok.com/@tattooedabroad" },
  { Icon: Instagram, label: "Instagram", handle: "@tattooedabroad", href: "https://www.instagram.com/tattooedabroad/" },
  { Icon: Facebook, label: "Facebook", handle: "@tattooedabroad", href: "https://www.facebook.com/profile.php?id=61592907290104" },
  { Icon: XIcon, label: "X", handle: "@tattooedabroad", href: "https://x.com/tattooedabroad" },
  { Icon: Mail, label: "Email", handle: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
];

const CARD_BASE =
  "group flex h-full flex-col items-center border border-white/10 bg-surface/40 px-5 py-6 text-center transition-all duration-300";
const CARD_HOVER =
  "cursor-pointer hover:border-flame/60 hover:bg-flame/[0.04] hover:shadow-[0_0_45px_-12px_rgba(255,122,26,0.45)]";

const CardInner = ({ Icon, label, handle }) => (
  <>
    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-flame transition-colors group-hover:border-flame group-hover:bg-flame/10">
      <Icon size={22} />
    </span>
    <p className="mt-4 font-gothic text-sm uppercase tracking-[0.25em] text-white">{label}</p>
    <p className="mt-1.5 min-h-[1.25rem] font-body text-sm text-white/50">{handle || ""}</p>
  </>
);

export default function Socials() {
  return (
    <>
      <Seo
        title="Follow Tattooed Abroad | YouTube & Social Media"
        description="Follow Tattooed Abroad on YouTube, Instagram, TikTok, Facebook, and X. Subscribe for updates as we build a documentary series about tattoos and travel."
      />
      <section className="relative overflow-hidden pt-20 pb-14 md:pt-24">
        <CornerWebs />
        <div className="pointer-events-none absolute left-1/2 top-1/3 h-[520px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-flame opacity-[0.12] blur-[150px]" />
        <div className="pointer-events-none absolute -right-20 bottom-0 h-[360px] w-[360px] rounded-full bg-crimson opacity-[0.10] blur-[150px]" />
        <div className="relative z-10 mx-auto max-w-6xl px-6 text-center md:px-10">
          <FadeUp>
            <p className="font-gothic text-xs uppercase tracking-[0.35em] text-flame">Follow the Journey</p>
            <h1 className="mt-4 font-display text-5xl font-light tracking-tight text-white md:text-7xl">
              <FlameInitial letter="S" size="0.86em" className="mr-[0.03em] translate-y-[0.04em]" />ocials
            </h1>
            <p
              className="mx-auto mt-5 max-w-3xl font-body text-base leading-relaxed md:whitespace-nowrap md:text-lg"
              style={{
                backgroundImage: "linear-gradient(90deg, #FFD700 0%, #FF7A1A 50%, #E2341E 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Follow the social channels for previews, footage, and stories from behind the series.
            </p>
          </FadeUp>

          <div className="mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-5 sm:grid-cols-2 md:mt-12 md:grid-cols-6">
            {CHANNELS.map((c, i) => (
              <FadeUp key={c.label} delay={i * 0.07} className="md:col-span-2">
                {c.href ? (
                  <a
                    href={c.href}
                    {...(c.href.startsWith("mailto") ? {} : { target: "_blank", rel: "noreferrer" })}
                    data-testid={`social-${c.label.toLowerCase()}`}
                    className={`${CARD_BASE} ${CARD_HOVER}`}
                  >
                    <CardInner {...c} />
                  </a>
                ) : (
                  <div data-testid={`social-${c.label.toLowerCase()}`} className={CARD_BASE}>
                    <CardInner {...c} />
                  </div>
                )}
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <CastingJoinSection source="socials" />
    </>
  );
}
